<script lang="ts">
  import { type Option, type Selection } from "@/types/scheduling";
  import { createCombobox, melt, type ComboboxOption, type ComboboxOptionProps } from "@melt-ui/svelte";
  import type { ChangeFn } from "@melt-ui/svelte/internal/helpers";
  import { fly } from "svelte/transition";
  import Check from "~icons/ion/checkmark-sharp";
  import ChevronDown from "~icons/ion/chevron-down";
  import ChevronUp from "~icons/ion/chevron-up";

  let {
    options,
    onSelect,
    key,
    showValues = true,
    selection,
  }: {
    options: Option[];
    onSelect: (selection: Selection) => void;
    key: string;
    showValues?: boolean;
    selection: Selection;
  } = $props();

  const handleSelection: ChangeFn<ComboboxOption<Selection> | undefined> = ({ curr, next }) => {
    if (next?.value) {
      onSelect({ value: next?.value.value, label: next?.value.label, key });
    }
    return next;
  };

  const toOption = (option: Option): ComboboxOptionProps<Option> => ({
    value: option,
    label: option.label,
  });

  const {
    elements: { menu, input, option, label },
    states: { open, inputValue, touchedInput, selected },
    helpers: { isSelected },
  } = createCombobox<Selection>({
    forceVisible: true,
    onSelectedChange: handleSelection,
  });

  $effect(() => {
    if (!$open) {
      $inputValue = $selected?.label || selection.label || "";
    }
  });

  let filteredData = $derived.by(() => {
    return $touchedInput
      ? options.filter(({ value, label }) => {
          const normalizedInput = $inputValue.toLowerCase();
          return value.toLowerCase().includes(normalizedInput) || label.toLowerCase().includes(normalizedInput);
        })
      : options;
  });

  let textLabel = $state("");
  if (key === "nutrition_area") {
    textLabel = "Please select your area of interest";
  }
  if (key === "communication_style") {
    textLabel = "Please select how you want to meet with your dietitian";
  }
</script>

<div class="combobox-container">
  <!-- svelte-ignore a11y_label_has_associated_control -->
  {#if textLabel && textLabel.length > 0}
    <label use:melt={$label}>
      <span class="combobox-label">{`${textLabel}`}</span>
    </label>
  {/if}

  <div class="combobox-input-container">
    <input use:melt={$input} class="combobox-input" placeholder={`Select an option`} />
    <div class="combobox-icon">
      {#if $open}
        <ChevronUp />
      {:else}
        <ChevronDown />
      {/if}
    </div>
  </div>
</div>
{#if $open}
  <ul class="combobox-menu" use:melt={$menu} transition:fly={{ duration: 150, y: -5 }}>
    <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
    <div class="combobox-menu-items" tabindex="0">
      {#each filteredData as optionItem, index (index)}
        <li class="combobox-menu-item" use:melt={$option(toOption(optionItem))}>
          {#if $isSelected(optionItem)}
            <div class="combobox-check">
              <Check />
            </div>
          {/if}
          <div class="combobox-menu-item-content">
            <span class="combobox-menu-item-label">{optionItem.label}</span>
            {#if showValues}
              <span class="combobox-menu-item-value">{optionItem.value}</span>
            {/if}
          </div>
        </li>
      {:else}
        <li class="combobox-no-item">No results found</li>
      {/each}
    </div>
  </ul>
{/if}

<style>
  .combobox-container {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    margin-bottom: 40px;
    width: 100%;
  }

  .combobox-container label {
    display: block;
    margin: 0;
    padding: 0;
  }

  .combobox-label {
    display: block;
    font-size: 15px;
    font-weight: 600;
    color: #333;
    margin-bottom: 20px;
  }

  .combobox-input-container {
    position: relative;
    max-width: 520px;
    width: 100%;
    margin: 0 auto;
  }

  .combobox-input {
    display: flex;
    align-items: center;
    min-width: 250px;
    justify-content: space-between;
    border-radius: 16px;
    border: 1px solid #dde4e1;
    background-color: white;
    padding: 32px 40px;
    color: #000;
    width: 100%;
  }

  .combobox-icon {
    display: flex;
    position: absolute;
    right: 36px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 10;
    color: #333;
  }

  .combobox-menu {
    z-index: 100000;
    display: flex;
    flex-direction: column;
    max-height: 300px;
    overflow: hidden;
    border-radius: 0.5rem;
    padding: 0;
  }

  .combobox-menu-items {
    display: flex;
    flex-direction: column;
    max-height: 100%;
    gap: 0;
    overflow-y: auto;
    background-color: #fff;
    padding-left: 0.5rem; /* px-2 */
    padding-right: 0.5rem; /* px-2 */
    padding-top: 0.5rem; /* py-2 */
    padding-bottom: 0.5rem; /* py-2 */
    color: #000;
  }

  .combobox-menu-item {
    position: relative;
    cursor: pointer;
    border-radius: 0.375rem;
    padding: 0.5rem 1rem; /* py-2 px-4 */
    transition:
      background-color 0.2s,
      color 0.2s;
    background-color: #fff;
    color: #000;
  }

  .combobox-menu-item:hover {
    background-color: #f7f7f7;
    color: #333;
  }

  .combobox-menu-item[data-highlighted] {
    background-color: #eee;
    color: #333;
  }

  .combobox-menu-item[data-disabled] {
    opacity: 0.5;
  }

  .combobox-no-item {
    position: relative; /* relative */
    cursor: pointer; /* cursor-pointer */
    border-radius: 0.375rem; /* rounded-md */
    padding-top: 0.25rem; /* py-1 */
    padding-left: 2rem; /* pl-8 */
    padding-right: 1rem; /* pr-4 */
  }

  .combobox-check {
    position: absolute;
    left: 0.5rem;
    top: 50%;
    z-index: 10;
    transform: translateY(-50%) translateY(1px);
    color: #555;
  }

  .combobox-menu-item-content {
    padding-left: 1rem; /* pl-4 */
  }

  .combobox-menu-item-label {
    font-weight: 500; /* font-medium */
  }

  .combobox-menu-item-value {
    display: block; /* block */
    font-size: 0.875rem; /* text-sm */
    opacity: 0.75; /* opacity-75 */
  }

  @media (max-width: 600px) {
    .combobox-label {
      font-size: 15px;
    }
  }
</style>
